import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { usePdfSetters } from '@/hooks/pdf/usePdfSetters'
import { useErrorHandler } from '@/hooks/utility/useErrorHandler'
import { useSetToast } from '@/hooks/utility/useSetToast'
import { EventCategory, logger } from '@/utils/logger'
import { loggerSentry } from '@/utils/loggerSentry'
import { useRecoilLogData } from '@/utils/recoilLogger'
import { buildUrlWithParams } from '@/utils/global/buildUrlWithParams'
import { STATUS_KEY, M3 } from '@messenger/consts'
import type { StatusKey } from '@messenger/types'
import type { AxiosError } from 'axios'
import type { UpdatePdfStateFunction, PdfState } from '@/hooks/pdf/usePdfViewer'

type UsePdfHandlersParams = Pick<
  PdfState,
  | 'generatingText'
  | 'generating'
  | 'shouldSetProductStatusToEditing'
  | 'shouldSetProductStatusToProofSent'
  | 'shouldSetProductStatusToFinalized'
> & {
  isUserAllowedToSeeFinalPdf: boolean
  pageSet: {
    id: number
  }
  params: URLSearchParams
  productStatus: StatusKey
  setProductStatus: (status: StatusKey) => Promise<void>
  selectedLayout: {
    paper_height: number
    paper_width: number
  }
  updatePdfState: UpdatePdfStateFunction
  generatePDF: (
    isProof: boolean,
    selectedLayout: {
      paper_height: number
      paper_width: number
    },
  ) => Promise<void>
  getOrderProducts: () => void
  cleanupPdfGeneration: () => void
  applicationType: string
}

export const usePdfHandlers = ({
  pageSet,
  params,
  productStatus,
  setProductStatus,
  selectedLayout,
  updatePdfState,
  generatePDF,
  getOrderProducts,
  cleanupPdfGeneration,
  generatingText,
  generating,
  isUserAllowedToSeeFinalPdf,
  shouldSetProductStatusToEditing,
  shouldSetProductStatusToProofSent,
  shouldSetProductStatusToFinalized,
  applicationType,
}: UsePdfHandlersParams) => {
  const { t } = useTranslation()
  const recoilLogData = useRecoilLogData()
  const handleError = useErrorHandler('usePdfHandlers')
  const setToast = useSetToast()

  const {
    setShowPdfViewer,
    setDisclaimerAcknowledged,
    setPendingCloseOperation,
    // delay setting product status for later, to avoid premature component re-renders
    setShouldSetProductStatusToFinalized,
    setShouldSetProductStatusToEditing,
    setShouldSetProductStatusToProofSent,
    setGeneratingText,
    setDisableCustomizeProductHeaderDropdowns,
    setCanCustomize,
    setCanRevertFinalization,
  } = usePdfSetters({ updatePdfState })

  const proofText =
    applicationType === M3 ? t('pdf.generateProofM3') : t('pdf.generateProof')

  const handleDeferredClose = useCallback(async () => {
    try {
      if (shouldSetProductStatusToEditing) {
        setShouldSetProductStatusToEditing(false)
        setGeneratingText(proofText)
        await setProductStatus(STATUS_KEY.EDITING)
      } else if (shouldSetProductStatusToProofSent) {
        setShouldSetProductStatusToProofSent(false)
        setGeneratingText(t('pdf.generatingProof'))
        await setProductStatus(STATUS_KEY.PROOF_SENT)
        setGeneratingText(proofText)
        setToast({
          icon: 'check',
          text: t('toast.successfullySentProof'),
        })
      } else if (shouldSetProductStatusToFinalized) {
        setShouldSetProductStatusToFinalized(false)
        setGeneratingText(t('pdf.finalizing'))
        await setProductStatus(STATUS_KEY.FINALIZED)
        setCanRevertFinalization(true)
        setGeneratingText(t('customizeProductHeader.revertFinalization'))
        setToast({
          icon: 'check',
          text: t('toast.successfullyFinalized'),
        })
      }
      setDisableCustomizeProductHeaderDropdowns(false)
      setDisclaimerAcknowledged(false)
    } catch (error: unknown) {
      cleanupPdfGeneration()
      logger.error({
        message: 'error_closing_pdf_viewer',
        category: EventCategory.ERROR,
        error: error as AxiosError | Error,
      })
      loggerSentry.error({
        message: 'error_closing_pdf_viewer',
        category: EventCategory.ERROR,
        error: error as AxiosError | Error,
      })
      handleError('error_closing_pdf_viewer', error as AxiosError | Error)
    }
  }, [
    shouldSetProductStatusToEditing,
    shouldSetProductStatusToProofSent,
    shouldSetProductStatusToFinalized,
    t,
    proofText,
    setProductStatus,
    setToast,
    handleError,
    setCanRevertFinalization,
    setDisableCustomizeProductHeaderDropdowns,
    setDisclaimerAcknowledged,
    setGeneratingText,
    setShouldSetProductStatusToEditing,
    setShouldSetProductStatusToFinalized,
    setShouldSetProductStatusToProofSent,
    cleanupPdfGeneration,
  ])

  const handleFinalizePdf = useCallback(() => {
    const orderIsClosed = productStatus === STATUS_KEY.CLOSED
    if (orderIsClosed) {
      setShowPdfViewer(false)
      getOrderProducts()
      return
    }
    setDisclaimerAcknowledged(false)
    setShouldSetProductStatusToEditing(false)
    setShouldSetProductStatusToProofSent(false)
    generatePDF(false, selectedLayout)
    return
  }, [
    productStatus,
    setShowPdfViewer,
    getOrderProducts,
    setDisclaimerAcknowledged,
    setShouldSetProductStatusToEditing,
    setShouldSetProductStatusToProofSent,
    generatePDF,
    selectedLayout,
  ])

  const handleClosePdfViewer = useCallback(async () => {
    setDisableCustomizeProductHeaderDropdowns(true)
    setShowPdfViewer(false)

    const orderIsClosed = productStatus === STATUS_KEY.CLOSED
    if (orderIsClosed) {
      getOrderProducts()
      return
    }

    if (generating) {
      setPendingCloseOperation(true)
      if (shouldSetProductStatusToEditing) {
        setGeneratingText(proofText)
      } else if (shouldSetProductStatusToProofSent) {
        setGeneratingText(t('pdf.generatingProof'))
      } else if (shouldSetProductStatusToFinalized) {
        setGeneratingText(t('pdf.finalizing'))
      }
      return
    }
    await handleDeferredClose()
  }, [
    setDisableCustomizeProductHeaderDropdowns,
    setShowPdfViewer,
    productStatus,
    getOrderProducts,
    generating,
    setPendingCloseOperation,
    shouldSetProductStatusToEditing,
    shouldSetProductStatusToProofSent,
    shouldSetProductStatusToFinalized,
    setGeneratingText,
    t,
    proofText,
    handleDeferredClose,
  ])

  const handleCustomizeClick = useCallback(async () => {
    setDisableCustomizeProductHeaderDropdowns(true)
    await setProductStatus(STATUS_KEY.EDITING)
    setDisableCustomizeProductHeaderDropdowns(false)
    logger.info({
      message: 'preview_customize_button_clicked',
      category: EventCategory.CLICK,
      data: {
        ...recoilLogData,
        navigatedTo: buildUrlWithParams(`/editor/${pageSet?.id}`, params),
      },
    })
  }, [
    setDisableCustomizeProductHeaderDropdowns,
    setProductStatus,
    recoilLogData,
    pageSet?.id,
    params,
  ])

  const handleGeneratePdfClick = useCallback(async () => {
    setDisableCustomizeProductHeaderDropdowns(true)
    const isGeneratingProof = generatingText === t('pdf.generatingProof')
    const isGeneratingFinal = generatingText === t('pdf.finalizing')
    if (isGeneratingProof) {
      setShowPdfViewer(true)
      return
    }
    if (isUserAllowedToSeeFinalPdf && isGeneratingFinal) {
      setShowPdfViewer(true)
      return
    }
    await generatePDF(true, selectedLayout)
    setDisableCustomizeProductHeaderDropdowns(false)
  }, [
    generatingText,
    generatePDF,
    isUserAllowedToSeeFinalPdf,
    selectedLayout,
    setShowPdfViewer,
    t,
    setDisableCustomizeProductHeaderDropdowns,
  ])

  const handleRevertFinalizationClick = useCallback(async () => {
    setDisableCustomizeProductHeaderDropdowns(true)
    setCanRevertFinalization(false)
    await setProductStatus(STATUS_KEY.EDITING)
    setGeneratingText(proofText)
    setCanCustomize(true)
    setDisableCustomizeProductHeaderDropdowns(false)
  }, [
    setDisableCustomizeProductHeaderDropdowns,
    setCanRevertFinalization,
    setProductStatus,
    setGeneratingText,
    proofText,
    setCanCustomize,
  ])

  const handleDropdownStatusChangeToEditing = useCallback(async () => {
    setDisableCustomizeProductHeaderDropdowns(true)
    try {
      await setProductStatus(STATUS_KEY.EDITING)
    } finally {
      setDisableCustomizeProductHeaderDropdowns(false)
      setCanCustomize(true)
      setCanRevertFinalization(false)
      setGeneratingText(proofText)
    }
  }, [
    setProductStatus,
    setDisableCustomizeProductHeaderDropdowns,
    setCanCustomize,
    setCanRevertFinalization,
    setGeneratingText,
    proofText,
  ])

  const handleDropdownStatusChangeToProofSent = useCallback(async () => {
    setDisableCustomizeProductHeaderDropdowns(true)
    try {
      await setProductStatus(STATUS_KEY.PROOF_SENT)
    } finally {
      setDisableCustomizeProductHeaderDropdowns(false)
      setCanCustomize(true)
      setCanRevertFinalization(false)
      setGeneratingText(proofText)
    }
  }, [
    setProductStatus,
    setDisableCustomizeProductHeaderDropdowns,
    setCanCustomize,
    setCanRevertFinalization,
    setGeneratingText,
    proofText,
  ])

  const handleDropdownStatusChangeToFinalized = useCallback(async () => {
    setDisableCustomizeProductHeaderDropdowns(true)
    try {
      await setProductStatus(STATUS_KEY.FINALIZED)
    } finally {
      setDisableCustomizeProductHeaderDropdowns(false)
      setCanRevertFinalization(true)
      setCanCustomize(false)
      setGeneratingText(t('customizeProductHeader.revertFinalization'))
    }
  }, [
    setProductStatus,
    setDisableCustomizeProductHeaderDropdowns,
    setCanRevertFinalization,
    setCanCustomize,
    setGeneratingText,
    t,
  ])

  const handleDropdownStatusChangeToClosed = useCallback(async () => {
    setDisableCustomizeProductHeaderDropdowns(true)
    try {
      await setProductStatus(STATUS_KEY.CLOSED)
    } finally {
      setDisableCustomizeProductHeaderDropdowns(false)
      setCanRevertFinalization(false)
      setCanCustomize(false)
    }
  }, [
    setProductStatus,
    setDisableCustomizeProductHeaderDropdowns,
    setCanRevertFinalization,
    setCanCustomize,
  ])

  const handleDropdownProductStatusChange = useCallback(
    async (newStatus: StatusKey) => {
      switch (newStatus) {
        case STATUS_KEY.EDITING:
          await handleDropdownStatusChangeToEditing()
          break
        case STATUS_KEY.PROOF_SENT:
          await handleDropdownStatusChangeToProofSent()
          break
        case STATUS_KEY.FINALIZED:
          await handleDropdownStatusChangeToFinalized()
          break
        case STATUS_KEY.CLOSED:
          await handleDropdownStatusChangeToClosed()
          break
        default:
          await setProductStatus(newStatus)
      }
    },
    [
      handleDropdownStatusChangeToEditing,
      handleDropdownStatusChangeToProofSent,
      handleDropdownStatusChangeToFinalized,
      handleDropdownStatusChangeToClosed,
      setProductStatus,
    ],
  )

  return {
    handleFinalizePdf,
    handleClosePdfViewer,
    handleCustomizeClick,
    handleGeneratePdfClick,
    handleRevertFinalizationClick,
    handleDeferredClose,
    handleDropdownProductStatusChange,
  }
}
